import {AfterViewChecked, ChangeDetectorRef, Compiler, Component, HostListener, OnInit, OnDestroy} from '@angular/core';
import {Router} from '@angular/router';
import {distinctUntilChanged, map} from 'rxjs/operators';
import {Subscription} from 'rxjs';
import {LocalStorageService} from '@core_services/local-storage.service';
import {UserIdleService} from 'angular-user-idle';
import * as temp from 'moment';
const moment = temp['default'];
import { HttpClient } from '@angular/common/http';

declare const walkMe: any;
import {TokenService} from '@core_services/token.service';
import { commonConstants } from '@shell_components/constants/commonConstants';
import {SharedService, SwitchThemeService, featureFlags} from '@ems/shared';
import { FeatureToggleClientService } from '@schwab/schwab-feature-toggle-angular';
import {DynamicEnvironment} from '@environment/dynamic-environment';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html'
})
export class AppComponent implements OnInit, AfterViewChecked, OnDestroy {

  /**
   * How long to wait after the maintenance flag reports "off" before actually
   * leaving the maintenance overlay. This rides out a momentary flag blip during
   * a feature-toggle reconnect so an active maintenance user is not bounced to
   * the workbench and straight back.
   */
  private static readonly MAINTENANCE_EXIT_CONFIRM_MS = 1500;

  env = new DynamicEnvironment();
  /** When true, the maintenance overlay is shown in place of the application. */
  maintenanceOn = false;
  private maintenanceSubscription: Subscription;
  private maintenanceExitTimer: ReturnType<typeof setTimeout> | undefined;

 constructor(public sharedService: SharedService,
  private localStorageService: LocalStorageService,
  private changeDetector: ChangeDetectorRef,
  private userIdle: UserIdleService,
  private tokenService: TokenService,
  private compiler: Compiler,
  public httpService: HttpClient,
  public switchThemeService: SwitchThemeService,
  private readonly featureToggleClient: FeatureToggleClientService,
  private readonly router: Router) {
  }

  ngOnInit() {
    /*this.checkSession();*/
    sessionStorage.clear();
    walkMe(this.env.production);
    this.sharedService.isProduction = this.env.production;
    this.sharedService.domainValue = this.env.domain;
    this.isUserAuthorized();
    this.compiler.clearCache();
    this.watchMaintenanceMode();
  }

  /**
   * Show or hide the maintenance experience based on the live
   * `strp.web.ops.maintenance-mode` flag.
   *
   * The maintenance screen is rendered as a full-page overlay at the app root
   * (see the template) rather than as a separate route. This deliberately
   * avoids Angular navigation, so:
   *  - turning maintenance on instantly covers the current screen without
   *    triggering `CanDeactivate` "unsaved changes" prompts, and
   *  - there is no shell teardown/rebuild or route-guard race.
   *
   * When maintenance clears, the app does a single full-page navigation to the
   * workbench so users land on a known landing page and pick up the freshly
   * deployed version instead of the stale bundle they had open. The
   * `APP_INITIALIZER` readiness gate guarantees the first emission here is the
   * real flag value, so the overlay never flickers on load.
   */
  private watchMaintenanceMode() {
    this.maintenanceSubscription = this.featureToggleClient
      .monitorFlag(featureFlags.maintenanceMode)
      .pipe(
        map(flagValue => flagValue === true),
        distinctUntilChanged()
      )
      .subscribe(maintenanceOn => {
        if (maintenanceOn) {
          // Entering or staying in maintenance: show the overlay and cancel any
          // pending exit (e.g. a transient feature-toggle reconnect that briefly
          // reported "off" and then re-asserted "on").
          clearTimeout(this.maintenanceExitTimer);
          this.maintenanceOn = true;
          return;
        }
        if (!this.maintenanceOn) {
          return;
        }
        // The stream reports "off" while the overlay is up. Keep the overlay and
        // defer: only leave maintenance if the flag is DEFINITIVELY off when the
        // window elapses (see exitMaintenanceIfConfirmed). This prevents a flaky
        // stream (transient/unevaluable emissions during a poll or reconnect)
        // from triggering a full-page reload loop.
        clearTimeout(this.maintenanceExitTimer);
        this.maintenanceExitTimer = setTimeout(
          () => this.exitMaintenanceIfConfirmed(),
          AppComponent.MAINTENANCE_EXIT_CONFIRM_MS
        );
      });
  }

  /**
   * Confirm the maintenance flag is genuinely off — via the authoritative
   * synchronous `evaluateFlag` read — before doing the disruptive full-page
   * reload onto the latest bundle. A sentinel default distinguishes a real
   * `false` from a value the client cannot currently evaluate
   * (offline/reconnecting), so only a definitive "off" reloads; anything else
   * keeps the overlay and waits for the next stable emission.
   */
  private exitMaintenanceIfConfirmed(): void {
    const UNEVALUATED = 'unevaluated';
    const value = this.featureToggleClient.evaluateFlag(featureFlags.maintenanceMode, UNEVALUATED);
    if (value === false) {
      this.returnToLiveApp();
    }
  }

  /** Full-page navigation to the workbench. Wrapped in a method for testability. */
  protected returnToLiveApp(): void {
    window.location.assign('/ems/workbench');
  }

  ngOnDestroy() {
    if (this.maintenanceSubscription) {
      this.maintenanceSubscription.unsubscribe();
    }
    clearTimeout(this.maintenanceExitTimer);
  }

  ngAfterViewChecked() {
    this.changeDetector.detectChanges();
  }


  // checkSession() {
  //   this.userIdle.startWatching();
  //
  //   this.userIdle.onTimerStart().subscribe(() => {
  //   });
  //
  //   this.userIdle.onTimeout().subscribe(() => {
  //     location.reload();
  //   });
  //
  //   const loginTime = moment(new Date());
  //   this.userIdle.ping$.subscribe((count) => {
  //     const currentTime = moment(new Date());
  //     const diff = moment.duration(currentTime.diff(loginTime)).asMinutes();
  //     if (parseInt(diff, 10) > 55) {
  //       localStorage.clear();
  //       this.tokenService.getNewToken();
  //     }
  //   });
  // }

  isUserAuthorized() {
    if (window.location.pathname === '/unauthorized') {
      setTimeout(() => {
        localStorage.clear();
        this.tokenService.getNewToken();
        window.location.pathname = '/';
      }, commonConstants.delay);

    }
  }
}
